/**
 * Translates PayWay/Decidir SDK error messages from English to Spanish
 * Based on common error types and messages from Decidir API
 */

interface PayWayError {
  error: {
    type: string;
    message: string;
    param: string;
  };
}

/**
 * Maps common PayWay error messages and types to Spanish translations
 */
const PAYWAY_ERROR_TRANSLATIONS: Record<string, string> = {
  // Card number errors
  'invalid card_number': 'El número de tarjeta es inválido',
  'card_number is required': 'El número de tarjeta es requerido',
  'card_number invalid': 'El número de tarjeta es inválido',
  'invalid card number': 'El número de tarjeta es inválido',

  // Expiration date errors
  'invalid card_expiration_month': 'El mes de vencimiento es inválido',
  'invalid card_expiration_year': 'El año de vencimiento es inválido',
  'card_expiration_month is required': 'El mes de vencimiento es requerido',
  'card_expiration_year is required': 'El año de vencimiento es requerido',
  'invalid expiration date': 'La fecha de vencimiento es inválida',
  'expired card': 'La tarjeta está vencida',
  'card expired': 'La tarjeta está vencida',

  // CVV/Security code errors
  'invalid security_code': 'El código de seguridad (CVV) es inválido',
  'security_code is required': 'El código de seguridad es requerido',
  'invalid cvv': 'El código de seguridad (CVV) es inválido',
  'invalid cvn': 'El código de seguridad (CVV) es inválido',

  // Card holder errors
  'invalid card_holder_name': 'El nombre del titular es inválido',
  'card_holder_name is required': 'El nombre del titular es requerido',
  'invalid card holder name': 'El nombre del titular es inválido',

  // Document errors
  'invalid card_holder_doc_type': 'El tipo de documento es inválido',
  'invalid card_holder_doc_number': 'El número de documento es inválido',
  'card_holder_doc_number is required': 'El número de documento es requerido',
  'invalid document number': 'El número de documento es inválido',

  // General card errors
  'invalid card data': 'Los datos de la tarjeta son inválidos',
  'card declined': 'La tarjeta fue rechazada',
  'transaction declined': 'La transacción fue rechazada',
  'insufficient funds': 'Fondos insuficientes en la tarjeta',
  'card not supported': 'Este tipo de tarjeta no está soportada',
  'invalid card type': 'Este tipo de tarjeta no está soportada',
  'card not authorized': 'La tarjeta no está autorizada',
  'invalid transaction': 'Los datos de la transacción son inválidos',

  // Error types
  invalid_request_error: 'Solicitud inválida',
  card_error: 'Error en los datos de la tarjeta',
  api_error: 'Error del sistema de pagos',
  authentication_error: 'Error de autenticación',
  rate_limit_error: 'Demasiadas solicitudes, intentá nuevamente',
};

/**
 * Translates PayWay error message to Spanish
 * @param error - PayWay error object from SDK
 * @returns Spanish error message
 */
export const translatePayWayError = (error: PayWayError | undefined): string => {
  if (!error?.error) {
    return 'Error al procesar la tarjeta';
  }

  const { type, message, param } = error.error;

  // Try to match the full message first (most specific)
  const messageLower = message.toLowerCase().trim();
  if (PAYWAY_ERROR_TRANSLATIONS[messageLower]) {
    return PAYWAY_ERROR_TRANSLATIONS[messageLower];
  }

  // Try to match error type
  const typeLower = type.toLowerCase().trim();
  if (PAYWAY_ERROR_TRANSLATIONS[typeLower]) {
    return PAYWAY_ERROR_TRANSLATIONS[typeLower];
  }

  // Try to match common patterns in the message
  for (const [key, translation] of Object.entries(PAYWAY_ERROR_TRANSLATIONS)) {
    if (messageLower.includes(key.toLowerCase())) {
      return translation;
    }
  }

  // If param is available, try to create a contextual message
  if (param) {
    const paramLower = param.toLowerCase();
    if (paramLower.includes('card_number') || paramLower.includes('card number')) {
      return 'El número de tarjeta es inválido';
    }
    if (paramLower.includes('expiration') || paramLower.includes('expiry')) {
      return 'La fecha de vencimiento es inválida';
    }
    if (paramLower.includes('security_code') || paramLower.includes('cvv') || paramLower.includes('cvn')) {
      return 'El código de seguridad es inválido';
    }
    if (paramLower.includes('card_holder') || paramLower.includes('cardholder')) {
      return 'Los datos del titular son inválidos';
    }
    if (paramLower.includes('doc_number') || paramLower.includes('document')) {
      return 'El número de documento es inválido';
    }
  }

  // Fallback to generic message
  return 'Error al procesar la tarjeta. Verificá los datos e intentá nuevamente';
};

/**
 * Translates an array of PayWay errors (takes the first one)
 * @param errors - Array of PayWay error objects from SDK
 * @returns Spanish error message
 */
export const translatePayWayErrors = (errors: PayWayError[] | undefined): string => {
  if (!errors || errors.length === 0) {
    return 'Error al procesar la tarjeta';
  }

  return translatePayWayError(errors[0]);
};
